import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import notificationIcon from '@/components/notification-icon/notification-icon.vue';
import constants from '@/constants';
import { formMixin, validators } from '@/components/forms/mixin';
import imageCardAmerican from '@/assets/images/cards/americanexpress.jpg';
import imageCardDiscover from '@/assets/images/cards/discover.jpg';
import imageCardMasterCard from '@/assets/images/cards/mastercard.jpg';
import imageCardVisa from '@/assets/images/cards/visa.jpg';
import imageCardBlank from '@/assets/images/cards/blank.png';
import getValidationMessage from '@/helpers/getValidationMessage';
import loaderMixin from '@/mixins/loader';
import accountVerifyMixin from '@/mixins/accountVerify';
import documentUploadMixin from '@/mixins/documentUpload';
import popupPageModeMixin from '@/mixins/popupPageModeMixin';
import contentLoaderMixin from '@/mixins/contentLoader';
import promoCodeMixin from '@/mixins/promoCode';
import depositWarningMixin from '@/mixins/depositWarningMixin';
import { getCardTypeForNumber, isNumber } from '@/utils/helpers';
import { MODALS } from '@/components/app/app-modals/app-modals';


const AMOUNT_STEP_ONE = 10;
const AMOUNT_STEP_THREE = 50;
const AMOUNT_STEP_FOUR = 100;
const DEPOSIT_INITIAL = 20;
const DEPOSIT_NON_ZERO_BALANCE = 50;

const STEP_INITIAL = 1;
const STEP_CONFIRM = 2;
const STEP_TRY_AGAIN = 3;


export default {
  mixins: [
    accountVerifyMixin,
    contentLoaderMixin,
    documentUploadMixin,
    formMixin,
    loaderMixin,
    popupPageModeMixin,
    promoCodeMixin,
    depositWarningMixin,
  ],
  validations() {
    const rules = {
      fields: {
        name: validators.required,
        number: validators.cardNumber,
        expiryDateMonth: validators.required,
        expiryDateYear: validators.required,
        cvv: validators.cvv,
        amount: validators.depositAmountCoinbase(
          this.minimumDepositAmount, this.maximumDepositAmount),
      },
    };

    if (this.avsFlag) {
      rules.fields.address = validators.required;
      rules.fields.city = validators.required;
      rules.fields.zipCode = validators.validateUSZipCode('country');
    }
    return rules;
  },
  components: {
    notificationIcon,
  },
  props: {
    instantCreditData: {},
  },
  data() {
    return {
      ready: false,
      canDeposit: false,
      balanceChecked: false,
      step: STEP_INITIAL,
      years: constants.expiryYears,
      months: constants.months,
      days: constants.days,
      depositAmounts: [
        AMOUNT_STEP_ONE,
        AMOUNT_STEP_THREE,
        AMOUNT_STEP_FOUR,
      ],
      disclaimer: '',
      errorMessage: '',
      errorMessageDepositFail: '',
      avsMessage: false,
      avsFlag: false,
      fields: {
        country: '',
        name: '',
        amount: '',
        number: '',
        expiryDateMonth: '',
        expiryDateYear: '',
        cvv: '',
        address: '',
        city: '',
        zipCode: '',
      },
      instantCreditDetail: this.instantCreditData,
    };
  },
  created() {
    this.initialize();
  },
  computed: {
    ...mapGetters('app', ['isLayoutDesktop', 'isLayoutMobile']),
    ...mapState('wallet', ['balance', 'firstDepositDone', 'depositMethods']),
    ...mapState('user', ['status', 'userData', 'ssnInfoVerified']),
    classModifiers() {
      return {
        'add-funds': true,
        'add-funds--popup': this.isPopup,
        'add-funds--regular': !this.isPopup,
      };
    },
    availableCardTypes() {
      return (this.depositMethods.creditCardTypes || []).map(i => i.toLowerCase());
    },
    minimumDepositAmount() {
      return this.depositMethods.minimumDepositAmount || 10.0;
    },
    maximumDepositAmount() {
      return 99999;
    },
    accountUploadDocumentTheme() {
      return this.isPage ? 'page-padded' : '';
    },
    minDeposit() {
      return AMOUNT_STEP_ONE;
    },
    amountHasError() {
      return this.$v.fields.amount.$error;
    },
    amountValidationMessage() {
      return getValidationMessage(this.$v.fields.amount);
    },
    isConfirmDisabled() {
      return this.invalidOrDisabled || !this.canDeposit || this.cardTypeHasError;
    },
    isStepInitial() {
      return this.step === STEP_INITIAL;
    },
    isStepConfirm() {
      return this.step === STEP_CONFIRM;
    },
    isStepTryAgain() {
      return this.step === STEP_TRY_AGAIN;
    },
    cardNumber() {
      return (this.fields.number || '').replace(/ /g, '');
    },
    cardTypeHasError() {
      return this.cardType !== constants.CARDS.BLANK &&
        !this.availableCardTypes.includes(this.cardType);
    },
    cardTypeValidationMessage() {
      return 'This card is not supported';
    },
    cardType() {
      return getCardTypeForNumber(this.cardNumber);
    },
    cardTypeImage() {
      let img = imageCardBlank;
      switch (this.cardType) {
        case constants.CARDS.AMERICAN_EXPRESS:
          img = imageCardAmerican;
          break;
        case constants.CARDS.DISCOVER:
          img = imageCardDiscover;
          break;
        case constants.CARDS.MASTERCARD:
          img = imageCardMasterCard;
          break;
        case constants.CARDS.VISA:
          img = imageCardVisa;
          break;
        default:
          break;
      }
      return img;
    },
    depositPayload() {
      const payload = {
        cardHoldersName: this.fields.name,
        amount: this.fields.amount,
        cardNumber: this.cardNumber,
        securityCode: this.fields.cvv,
        expirationMonth: this.fields.expiryDateMonth.value,
        expirationYear: this.fields.expiryDateYear.value,
        depositPromoCode: '',
        marketingSource: '',
        marketingProgram: '',
        cardType: this.cardType,
      };
      if (this.avsFlag) {
        payload.address1 = this.fields.address;
        payload.city = this.fields.city;
        payload.postalCode = this.fields.zipCode;
      }
      return payload;
    },
    infoAmount() {
      const number = Number(this.depositPayload.amount).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `$${number}`;
    },
  },
  methods: {
    ...mapActions('wallet', [
      'getDepositCard', 'confirmDepositCard', 'submitDepositCard',
      'setCurrentDeposit', 'getBalance', 'getDepositMethods',
    ]),
    ...mapActions('popup', [
      'setGenericPopup', 'setCurrentPopup', 'setCurrentSmallPopup',
      'setCurrentPopupOptions'
    ]),
    initialize() {
      this.loaderAdd();
      this.getBalance()
        .then(this.getDepositCard)
        .then((data) => {
          this.loaderRemove();
          this.isComponentReady = true;
          this.checkDocumentUploadFlags(data);
          this.canDeposit = data.canDeposit;
          this.errorMessage = data.errorMessage;
          this.promoCodeDetail = data.promoCodeDetail || {};
          this.disclaimer = data.disclaimer;
          this.fields.name = data.cardHoldersName;
          this.avsFlag = data.avsFlag;
          this.fields.city = data.city || '';
          this.fields.address = data.address1 || '';
          this.fields.zipCode = data.postalCode || '';
          this.fields.country = {
            value: this.userData.countryCode,
          };
        })
        .catch((error) => {
          this.isComponentReady = true;
          this.errorMessage = get(error, 'response.data.message', null);
          this.loaderRemove();
        });

      if (!this.isPopup) {
        this.getDepositMethods();
      }
    },
    tabChange(name) {
      this.activeTab = name;
    },
    scrollToTopIfPage() {
      if (this.isLayoutMobile && this.isPage) {
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);
      }
    },
    getInitialAmount() {
      return (this.balance && this.balance.accountBalanceDecimal > 0) ?
        DEPOSIT_NON_ZERO_BALANCE :
        DEPOSIT_INITIAL;
    },
    handleDepositFail(error) {
      this.errorMessageDepositFail = get(error, 'response.data.message', '');
      if (this.isPage) {
        this.step = STEP_INITIAL;
        this.setGenericPopup({
          name: MODALS.PAYMENT_ERROR,
          options: {
            title: 'Payment Failed',
            icon: 'error',
            error: this.errorMessageDepositFail,
          },
        });
      } else {
        this.step = STEP_TRY_AGAIN;
      }
    },
    onUpdateAmount(amount) {
      if (this.fields.amount === '') {
        this.fields.amount = amount;
      } else if (isNumber(this.fields.amount)) {
        this.fields.amount = Number(this.fields.amount) + amount;
      }
      this.$gtm.trackEvent({
        event: 'eventTrackerTrigger',
        category: 'Deposit',
        action: 'click',
        label: `Amount ${amount} Button`,
      });
    },
    onConfirmDeposit() {
      this.avsMessage = false;
      this.$gtm.trackEvent({
        event: 'eventTrackerTrigger',
        category: 'Deposit',
        action: 'submit',
        label: 'Submit Deposit',
      });
      if (this.formIsValid() && this.formDisable()) {
        this.loaderAdd();
        this.confirmDepositCard(this.depositPayload)
          .then((data) => {
            this.loaderRemove();
            this.formEnable();
            if (this.checkDocumentUploadFlags(data)) return;
            if (data.dapFlag) {
              this.step = STEP_INITIAL;
              this.canDeposit = false;
              this.errorMessage = data.dapMessage;
            } else if (data.avsFlag) {
              this.step = STEP_INITIAL;
              this.avsFlag = true;
              this.avsMessage = true;
              this.getDepositCard()
                .then((depositData) => {
                  this.checkDocumentUploadFlags(depositData);
                  this.fields.city = depositData.city || '';
                  this.fields.address = depositData.address1 || '';
                  this.fields.zipCode = depositData.postalCode || '';
                  this.fields.country = {
                    value: this.userData.countryCode,
                  };
                })
                .catch((error) => {
                  this.errorMessage = get(error, 'response.data.message', null);
                });
            } else {
              this.step = STEP_CONFIRM;
              this.scrollToTopIfPage();
            }
          })
          .catch((error) => {
            this.loaderRemove();
            if (this.checkDocumentUploadFlags(error.response.data)) return;
            this.formEnable();
            this.handleDepositFail(error);
          });
      }
    },
    onSubmitDeposit() {
      this.formDisable();
      this.$gtm.trackEvent({
        event: 'eventTrackerTrigger',
        category: 'Deposit Confirm',
        action: 'submit',
        label: 'Confirm Deposit',
      });
      this.loaderAdd();
      this.submitDepositCard(this.depositPayload)
        .then((data) => {
          this.loaderRemove();
          if (this.checkDocumentUploadFlags(data)) return;
          this.setCurrentDeposit(data);
          this.balanceChecked = false;
          this.$gtm.trackEvent({ event: this.status.hasBeenFunded ? 'MultiDeposit' : 'Deposit' });
          const popupOptions = {
            name: MODALS.PAYMENT_SUCCESS,
            options: {
              title: 'Payment Confirmed',
              icon: 'success',
              iconTitle: 'Success!',
            },
          };
          if (!this.status.hasBeenFunded) {
            popupOptions.options.noClose = true;
          }
          this.setGenericPopup(popupOptions);
          this.formEnable();
          this.$emit('success');
        })
        .then(this.getBalance)
        .catch((error) => {
          this.loaderRemove();
          if (this.checkDocumentUploadFlags(error.response.data)) return;
          this.formEnable();
          this.handleDepositFail(error);
        });
    },
    onTryAgain() {
      this.step = STEP_INITIAL;
    },
    showCVVModal() {
      this.setCurrentSmallPopup(MODALS.SMALL_CVV);
    },
    fieldName(name) {
      return `${this.mode}-${name}`;
    },
    handleAmountBlur() {
      this.$gtm.trackEvent({
        event: 'eventTrackerTrigger',
        category: 'Deposit',
        action: 'fill',
        label: 'Amount',
      });
    },
  },
  watch: {
    ssnInfoVerified() {
      this.initialize();
    }
  }
};



// WEBPACK FOOTER //
// ./src/components/wallet/add-funds-card/add-funds-card.js